import type { LoginRequest, EmailLoginRequest, PhoneLoginRequest, EnterpriseLoginRequest, RegisterRequest, PasswordResetRequest, ProfileUpdateRequest, AuthResponse, User, PasswordResetConfirmRequest, PasswordChangeRequest, LoginDevice } from '@/types/auth'
// import apiClient from './client'
import { api } from './index'

// 认证相关API
export const authApi = {
  // 账号密码登录（支持传递可选Axios配置，如headers）
  login: async (credentials: LoginRequest, config?: any): Promise<AuthResponse> => {
    // 后端期望email字段，将username转为email
    const payload = {
      email: credentials.username,
      password: credentials.password
    }
    return await api.post<AuthResponse>('/login', payload, config)
  },

  // 邮箱登录
  emailLogin: async (credentials: EmailLoginRequest): Promise<AuthResponse> => {
    return await api.post<AuthResponse>('/login', { email: credentials.email, password: credentials.verificationCode })
  },

  // 手机号登录
  phoneLogin: async (credentials: PhoneLoginRequest): Promise<AuthResponse> => {
    return await api.post<AuthResponse>('/login', { email: credentials.phone, password: credentials.verificationCode })
  },

  // 企业登录
  enterpriseLogin: async (credentials: EnterpriseLoginRequest): Promise<AuthResponse> => {
    return await api.post<AuthResponse>('/login', { email: credentials.username, password: credentials.password })
  },

  // 注册
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const payload = {
      account: data.account,
      name: data.name || data.username,
      email: data.email,
      password: data.password,
      companyName: data.companyName,
      companyInfo: data.companyInfo || '',
      saasUrl: data.saasUrl
    }
    return await api.post<AuthResponse>('/register', payload)
  },

  // 登出
  logout: async (): Promise<void> => {
    await api.post<void>('/logout')
  },

  // 刷新token（方案B：从请求体传 refreshToken）
  refreshToken: async (refreshToken: string): Promise<AuthResponse> => {
    return await api.post<AuthResponse>('/refresh', { refreshToken })
  },

  // 获取当前用户信息
  getProfile: async (): Promise<User> => {
    return await api.get<User>('/user/info')
  },

  // 获取当前用户信息（别名，用于兼容）
  getCurrentUser: async (): Promise<AuthResponse> => {
    const user = await api.get<User>('/user/info')
    // 确保roles是数组格式，支持逗号分隔的角色字符串
    let roles: string[] = []
    if (user.role) {
      if (typeof user.role === 'string') {
        roles = user.role.includes(',') ? user.role.split(',').map(r => r.trim()) : [user.role]
      } else if (Array.isArray(user.role)) {
        roles = user.role as any
      }
    }
    return {
      user,
      token: '', // Token已在header中
      expiresIn: 7200,
      permissions: [],
      roles
    }
  },

  // 更新用户信息
  updateProfile: async (data: ProfileUpdateRequest): Promise<User> => {
    return await api.put<User>('/user/info', data)
  },

  // 获取登录设备列表
  getLoginDevices: async (): Promise<LoginDevice[]> => {
    return await api.get<LoginDevice[]>('/user/devices')
  },

  // 修改密码
  changePassword: async (data: PasswordChangeRequest): Promise<void> => {
    await api.post<void>('/password/change', data)
  },

  // 重置密码（发送重置链接到邮箱）
  resetPassword: async (data: PasswordResetRequest): Promise<void> => {
    await api.post<void>('/password/reset-request', data)
  },

  // 重置密码确认（带token与新密码）
  confirmResetPassword: async (data: PasswordResetConfirmRequest): Promise<void> => {
    await api.post<void>('/password/reset', data)
  },

  // 发送邮箱验证码
  sendEmailCode: async (email: string): Promise<void> => {
    await api.post<void>('/send-email-code', { email })
  },

  // 发送手机验证码
  sendPhoneCode: async (phone: string): Promise<void> => {
    await api.post<void>('/send-phone-code', { phone })
  }
}
